import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { PrimaryLinkButton } from 'apps/wrapper/src/components/shared/buttons/buttons.styles';
import { useEventBus } from 'apps/wrapper/src/hooks/use-event-bus';
import { AppointmentAvailability } from 'apps/wrapper/src/services/appointment/types/appointment-availability';
import { AppointmentTimeOfDay } from 'apps/wrapper/src/services/appointment/types/appointment-time-of-day';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useApptRequestsWidget } from '../../../../../hooks/use-appt-requests-widget';
import { useRequestDetailsView } from '../../../../../hooks/use-request-details-view';
import { getProviderAndOperatoryStatus } from '../../../../../utils/provider-and-operatory-status';
import { MapCalendarRequest } from '../../../utils/calendar-request-mapper';
import { AvailabilityStatus } from './availability-status/availability-status';
import { RequestDate, RequestDatesListItem, RequestDateToolbar, RequestDateWrapper } from './request-date-item.styles';

const TIME_OF_DAY_CHIP = {
  [AppointmentTimeOfDay.Morning]: {
    label: 'Morning',
    icon: <WbSunnyOutlinedIcon sx={{ fontSize: '12px !important' }} />,
    sx: { backgroundColor: '#fbeacb', color: '#8a5a13' },
  },
  [AppointmentTimeOfDay.Afternoon]: {
    label: 'Afternoon',
    icon: <AccessTimeOutlinedIcon sx={{ fontSize: '12px !important' }} />,
    sx: { backgroundColor: '#d4e9f7', color: '#2068a6' },
  },
  [AppointmentTimeOfDay.Evening]: {
    label: 'Evening',
    icon: <NightsStayOutlinedIcon sx={{ fontSize: '12px !important' }} />,
    sx: { backgroundColor: '#e1e2f7', color: '#3f47a8' },
  },
} as const;

function getTimeOfDayChipConfig(dateStart: string) {
  const hour = moment(dateStart).hour();
  if (hour < 12) return TIME_OF_DAY_CHIP[AppointmentTimeOfDay.Morning];
  if (hour < 16) return TIME_OF_DAY_CHIP[AppointmentTimeOfDay.Afternoon];
  return TIME_OF_DAY_CHIP[AppointmentTimeOfDay.Evening];
}

export interface RequestDateSectionProps {
  requestTime: { dateStart: string; dateEnd: string };
  isDirectlyBooked?: boolean;
}

export const RequestDateItem: FC<RequestDateSectionProps> = (props) => {
  const { requestTime, isDirectlyBooked } = props;
  const { publishToIframes } = useEventBus();
  const { goToCalendarApp } = useApptRequestsWidget();
  const { requestDetails, isEditable, appointmentAvailabilities } = useRequestDetailsView();
  const [onSelecting, setOnSelecting] = useState(false);
  const [onPreviewing, setOnPreviewing] = useState(false);
  const [appointmentAvailability, setAppointmentAvailability] = useState<AppointmentAvailability | null | undefined>();

  useEffect(() => {
    if (appointmentAvailabilities) {
      const availability = appointmentAvailabilities.find(
        (x: AppointmentAvailability) =>
          moment(x.dateStart).isSame(moment(requestTime.dateStart)) &&
          moment(x.dateEnd).isSame(moment(requestTime.dateEnd))
      );

      setAppointmentAvailability(availability ?? null);
    }
  }, [appointmentAvailabilities]);

  const handlePreviewClick = async () => {
    if (!requestDetails) return;

    setOnPreviewing(true);
    await goToCalendarApp();

    setTimeout(() => {
      publishToIframes({
        messageType: 'appt-request-preview',
        data: {
          id: requestDetails.id,
          startDate: new Date(requestTime.dateStart),
          endDate: new Date(requestTime.dateEnd)
        }
      });
    }, 200);

    setTimeout(() => setOnPreviewing(false), 1000);
  };

  const handleSelectClick = async () => {
    if (!requestDetails) return;

    setOnSelecting(true);
    await goToCalendarApp();

    setTimeout(() => {
      publishToIframes({
        messageType: 'appt-request-schedule',
        data: MapCalendarRequest(requestDetails, requestTime)
      });
    }, 200);
    setTimeout(() => setOnSelecting(false), 1000);
  };

  return (
    <RequestDatesListItem>
      <RequestDateWrapper>
        <RequestDate>{moment(requestTime.dateStart).format('MM/DD/YYYY [at] h:mm a')}</RequestDate>
        {isEditable && (
          <AvailabilityStatus
            isLoading={appointmentAvailability === undefined}
            isAvailable={appointmentAvailability?.isAvailable}
            availabilityUnknown={appointmentAvailability === null}
          />
        )}
      </RequestDateWrapper>

      <Box sx={{ marginTop: '5px' }}>
        {getProviderAndOperatoryStatus(requestDetails?.provider?.name, requestDetails?.operatory?.description)}
      </Box>

      {appointmentAvailability === undefined && (
        <RequestDateToolbar>
          <Skeleton variant="rounded" width={40} height={16} sx={{ marginRight: '10px' }} />
          <Skeleton variant="rounded" width={50} height={16} />
        </RequestDateToolbar>
      )}

      {appointmentAvailability && isEditable && (
        <RequestDateToolbar>
          <PrimaryLinkButton
            variant="text"
            onClick={handleSelectClick}
            loading={onSelecting}
            disabled={appointmentAvailability?.isAvailable == false || onPreviewing || onSelecting}
            sx={{ marginRight: '10px' }}
            disableRipple
          >
            select
          </PrimaryLinkButton>
          <PrimaryLinkButton
            loading={onPreviewing}
            onClick={handlePreviewClick}
            disabled={appointmentAvailability?.isAvailable == false || onPreviewing || onSelecting}
            disableRipple
          >
            preview
          </PrimaryLinkButton>
        </RequestDateToolbar>
      )}
      {isDirectlyBooked && (
        <RequestDateToolbar>
          <PrimaryLinkButton loading={onPreviewing} onClick={handlePreviewClick} disabled={onPreviewing} disableRipple>
            view appointment
          </PrimaryLinkButton>
        </RequestDateToolbar>
      )}

      {!isDirectlyBooked && (() => {
        const chipConfig = getTimeOfDayChipConfig(requestTime.dateStart);
        return (
          <Box sx={{ pt: 1, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.75 }}>
              Preferred time of day
            </Typography>
            <Chip
              size="small"
              label={chipConfig.label}
              icon={chipConfig.icon}
              sx={{
                height: 24,
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 400,
                pl: 0.25,
                ...chipConfig.sx,
                '& .MuiChip-icon': {
                  color: 'inherit',
                  ml: '6px',
                  mr: '-2px',
                },
              }}
            />
          </Box>
        );
      })()}
    </RequestDatesListItem>
  );
};
